import React, { useState } from 'react'
import { cn } from 'lib/utils'
import formatReceiptTime from 'features/Chat/utils/formatReceiptTime'
import type { IChatHeader, IChatMessage, IReadStateRow } from 'features/Chat/Chat.types'

export interface Props {
	/** The newest outgoing message in the thread. */
	message: IChatMessage
	header?: IChatHeader
	readState?: IReadStateRow[]
	userId: string
	className?: string
}

const participantsOf = (header?: IChatHeader, userId?: string) => {
	if (!header) return [] as { _id: string; name: string }[]
	const everyone = [
		header.senderId,
		...(header.receiverIds || []).map(r => r.receiverId),
	].filter(Boolean) as { _id: string; name: string }[]

	// De-duplicate: a participant can appear as both sender and receiver across
	// the header's history.
	const seen = new Set<string>()
	return everyone.filter(p => {
		const id = String(p._id)
		if (id === String(userId) || seen.has(id)) return false
		seen.add(id)
		return true
	})
}

const atOrAfter = (mark: string | undefined, at: string) =>
	!!mark && new Date(mark).getTime() >= new Date(at).getTime()

/**
 * The single trailing line under the last outgoing message: Sent, Delivered,
 * "Read 10:42 AM", or in a group "Read by 2 of 5".
 *
 * Exactly one of these renders per thread. iOS shows one, and a label under
 * every bubble turns a conversation into a receipt log.
 */
const DeliveryStatus = ({ message, header, readState = [], userId, className = '' }: Props) => {
	const [expanded, setExpanded] = useState(false)

	if (message.deliveryStatus === 'sending') {
		return <Line className={className}>Sending…</Line>
	}
	if (message.deliveryStatus === 'failed') {
		return (
			<Line className={cn('text-red-600', className)}>Not Delivered</Line>
		)
	}

	const others = participantsOf(header, userId)
	const rowFor = (id: string) => readState.find(r => String(r.userId) === String(id))

	const readers = others.filter(p => atOrAfter(rowFor(p._id)?.lastReadAt, message.createdAt))
	const delivered = others.some(
		p =>
			atOrAfter(rowFor(p._id)?.lastDeliveredAt, message.createdAt) ||
			atOrAfter(rowFor(p._id)?.lastReadAt, message.createdAt),
	)

	if (readers.length === 0) {
		return <Line className={className}>{delivered ? 'Delivered' : 'Sent'}</Line>
	}

	// 1:1 gets the iOS wording. Groups get a count, which iOS does not show and
	// which is the more useful answer when five people are on the thread.
	if (others.length === 1) {
		const at = formatReceiptTime(rowFor(others[0]._id)?.lastReadAt)
		return <Line className={className}>{at ? `Read ${at}` : 'Read'}</Line>
	}

	const label =
		readers.length === others.length
			? `Read by ${readers.length}`
			: `Read by ${readers.length} of ${others.length}`

	return (
		<div className={cn('flex flex-col items-end pr-1', className)}>
			<button
				type="button"
				onClick={() => setExpanded(v => !v)}
				aria-expanded={expanded}
				className="text-[11px] text-gray-400 hover:text-gray-600"
			>
				{label}
			</button>
			{expanded && (
				<ul className="mt-0.5 list-none space-y-0.5 p-0 text-right text-[11px] text-gray-400">
					{readers.map(p => (
						<li key={p._id}>
							{p.name} · {formatReceiptTime(rowFor(p._id)?.lastReadAt)}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

const Line = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
	<div className={cn('pr-1 text-right text-[11px] text-gray-400', className)}>{children}</div>
)

export default DeliveryStatus
